import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { platformApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { Badge } from '@/components/ui/badge';
import { PageLoading } from '@/components/ui/loading';
import { Pagination } from '@/components/ui/pagination';
import { formatDate } from '@/lib/utils';

interface AuditRow {
  id: string;
  user_id: string;
  organization_id: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  details: string | null;
  created_at: string;
}

const PAGE_SIZE = 10;

export default function PlatformAuditLogsPage() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'audit-logs', page],
    queryFn: async () => {
      const r = await platformApi.get<{ success: boolean; data: AuditRow[]; total: number }>(`/platform/audit-logs?page=${page}&limit=${PAGE_SIZE}`);
      return r;
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">Platform Audit Logs</h1>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableIndexHead /><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>User</TableHead><TableHead>Org</TableHead><TableHead>Details</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(data?.data ?? []).map((log, index) => (
                <TableRow key={log.id}>
                  <TableIndexCell value={rowSerial(index, page, PAGE_SIZE)} />
                  <TableCell className="text-sm text-muted whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                  <TableCell><Badge variant="info">{log.action}</Badge></TableCell>
                  <TableCell className="text-sm font-mono">{log.user_id?.slice(0, 8) || '—'}</TableCell>
                  <TableCell className="text-sm font-mono">{log.organization_id?.slice(0, 8) || 'platform'}</TableCell>
                  <TableCell className="text-sm text-secondary max-w-md truncate">{log.details || '—'}</TableCell>
                </TableRow>
              ))}
              {(data?.data ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted">No audit logs found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onPageChange={setPage} />
    </div>
  );
}
